// ── First and last name, held separately ─────────────────────────────────────
//
// Profile Information collects the two parts separately, but the account record
// has always stored ONE `name` string, and the Dashboard greeting, the header
// chip and the avatar initials all read it. So the split is real at the edges —
// the form and the API payload — while a joined `name` continues to be written,
// and nothing that reads it breaks.
//
// Accounts created before the split (and every Google sign-in, which hands us a
// single `full_name`) have no stored parts. They are derived by splitting on
// the FIRST space: the first token is the given name and the rest is the
// family name. No heuristic gets this right for every name on earth —
// "Ludwig van Beethoven" and "Juan Carlos Rodriguez" both defeat it — which is
// exactly why the two fields are editable rather than computed. This is the
// starting point a user corrects once, not a claim about their name.

const clean = (value) => String(value ?? '').trim().replace(/\s+/g, ' ');

/** Split a stored full name into { first, last }. */
export function splitPersonName(fullName) {
  const name = clean(fullName);
  if (!name) return { first: '', last: '' };
  const gap = name.indexOf(' ');
  if (gap === -1) return { first: name, last: '' }; // a mononym is a real name
  return { first: name.slice(0, gap), last: name.slice(gap + 1) };
}

/** Join the two parts back into the single stored `name`. */
export function joinPersonName(first, last) {
  return [clean(first), clean(last)].filter(Boolean).join(' ');
}

/**
 * The two parts for a user record.
 *
 * Prefers stored parts when the API has them, and falls back to splitting the
 * legacy single field — so this works before and after the backend keeps the
 * columns, without a migration flag to check.
 */
export function personNameFrom(userData) {
  const first = clean(userData?.first_name);
  const last = clean(userData?.last_name);
  if (first || last) return { first, last };
  return splitPersonName(userData?.name);
}

/**
 * Validate the pair. Returns an error message, or null when it is fine.
 *
 * A first name is required; a last name is not. Requiring one would lock out
 * users who have a single legal name, and the field this replaces never asked
 * for a surname either — a fix should not quietly raise the bar on people who
 * already have accounts. The two-character floor is the previous rule on the
 * whole name, kept.
 */
export function validatePersonName({ first, last }) {
  const f = clean(first);
  const l = clean(last);
  if (!f) return 'Enter your first name.';
  // Last name became REQUIRED. It was optional, and the form was honest about
  // that — an asterisk on First and none on Last.
  //
  // Worth stating the cost, because it is borne by real accounts: anyone whose
  // stored name is a single word (a Google sign-in that returned one token, or
  // a mononym) now has to supply a last name before they can save any other
  // profile change. The message therefore names the field and says what to do,
  // rather than refusing and leaving them to hunt for the empty box.
  if (!l) return 'Enter your last name.';
  if (joinPersonName(f, l).length < 2) return 'Name must be at least 2 characters.';
  return null;
}
